/**
 * Provider Directory Map Component
 * Interactive map displaying provider locations with markers
 * Uses Google Maps API
 */

import { useEffect, useRef, useState } from 'react';
import { Provider } from './ProviderResults';

interface ProviderMapProps {
  providers: Provider[];
  center?: { lat: number; lng: number };
  zoom?: number;
  onProviderClick: (provider: Provider) => void;
}

declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

export function ProviderMap({
  providers,
  center = { lat: 39.8283, lng: -98.5795 }, // Center of US
  zoom = 10,
  onProviderClick
}: ProviderMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement('script');
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      setError('Google Maps API key not configured');
      return;
    }

    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    script.onerror = () => setError('Failed to load Google Maps');
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) return;

    try {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true,
      });

      infoWindowRef.current = new window.google.maps.InfoWindow();
      setIsMapReady(true);
      console.log('🗺️ ProviderMap: Map initialized successfully');
    } catch (err) {
      setError('Failed to initialize map');
      console.error('Map initialization error:', err);
    }
  }, [isLoaded, center, zoom]);

  // Update markers when providers change or map becomes ready
  useEffect(() => {
    console.log('🗺️ ProviderMap: useEffect triggered', {
      isMapReady,
      hasMapInstance: !!mapInstanceRef.current,
      hasGoogle: !!window.google,
      providersCount: providers.length
    });

    if (!isMapReady || !mapInstanceRef.current || !window.google) {
      console.log('🗺️ ProviderMap: Map not ready, skipping marker update');
      return;
    }

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    if (providers.length === 0) {
      console.log('🗺️ ProviderMap: No providers to display');
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    const newMarkers: any[] = [];
    let skippedCount = 0;

    providers.forEach((provider, index) => {
      const lat = provider.latitude || 0;
      const lng = provider.longitude || 0;

      if (index < 3) {
        console.log(`🗺️ Provider ${index}: ${provider.organization_name}`, { lat, lng });
      }

      if (!lat || !lng) {
        skippedCount++;
        return;
      }

      const position = { lat, lng };
      bounds.extend(position);

      // Create custom marker icon based on organization type
      const markerIcon = getMarkerIcon(provider.organization_type);

      const marker = new window.google.maps.Marker({
        position,
        map: mapInstanceRef.current,
        title: provider.organization_name,
        icon: markerIcon,
        animation: window.google.maps.Animation.DROP,
      });

      // Create info window content
      const infoContent = createInfoWindowContent(provider);

      // Add click listener
      marker.addListener('click', () => {
        infoWindowRef.current.setContent(infoContent);
        infoWindowRef.current.open(mapInstanceRef.current, marker);
      });

      newMarkers.push(marker);
    });

    markersRef.current = newMarkers;
    console.log(`🗺️ ProviderMap: Created ${newMarkers.length} markers, skipped ${skippedCount} providers (no coords)`);

    // Fit map to show all markers
    if (newMarkers.length > 0) {
      mapInstanceRef.current.fitBounds(bounds);

      // Don't zoom in too much for single marker
      if (providers.length === 1) {
        const listener = window.google.maps.event.addListener(
          mapInstanceRef.current,
          'idle',
          () => {
            if (mapInstanceRef.current.getZoom() > 15) {
              mapInstanceRef.current.setZoom(15);
            }
            window.google.maps.event.removeListener(listener);
          }
        );
      }
    }
  }, [providers, isMapReady]);

  const getMarkerIcon = (organizationType: string) => {
    const iconColors: Record<string, string> = {
      'FQHC': '#3B82F6', // Blue
      'Free Clinic': '#10B981', // Green
      'Mental Health Center': '#8B5CF6', // Purple
      'Substance Abuse Treatment': '#F59E0B', // Amber
      'Food Bank': '#EF4444', // Red
      'Housing Support': '#6366F1', // Indigo
      'Transportation Provider': '#14B8A6', // Teal
      'ECM Provider': '#EC4899', // Pink
    };

    const color = iconColors[organizationType] || '#6B7280'; // Default gray

    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 2,
      scale: 10,
    };
  };

  const createInfoWindowContent = (provider: Provider) => {
    return `
      <div style="max-width: 300px; padding: 12px;">
        <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #111827;">
          ${provider.organization_name}
        </h3>
        <p style="margin: 0 0 8px 0; font-size: 14px; color: #6B7280;">
          ${provider.organization_type}
        </p>
        <p style="margin: 0 0 4px 0; font-size: 14px; color: #374151;">
          ${provider.street_address}<br>
          ${provider.city}, ${provider.state} ${provider.zip_code}
        </p>
        <p style="margin: 8px 0; font-size: 14px; color: #2563EB;">
          ${provider.primary_phone}
        </p>
        ${provider.distance_miles != null ? `
          <p style="margin: 8px 0; font-size: 14px; font-weight: 500; color: #059669;">
            ${provider.distance_miles.toFixed(1)} miles away
          </p>
        ` : ''}
        <button
          onclick="window.openProviderDetails('${provider.id}')"
          style="margin-top: 12px; padding: 8px 16px; background-color: #2563EB; color: white; border: none; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer;"
        >
          View Details
        </button>
      </div>
    `;
  };

  // Expose provider details function to window for info window buttons
  useEffect(() => {
    window.openProviderDetails = (providerId: string) => {
      const provider = providers.find(p => p.id === providerId);
      if (provider) {
        onProviderClick(provider);
      }
    };

    return () => {
      delete window.openProviderDetails;
    };
  }, [providers, onProviderClick]);

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex flex-col items-center justify-center space-y-4 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">⚠️</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Map Error</h3>
          <p className="text-gray-600 max-w-md">{error}</p>
          <p className="text-sm text-gray-500">
            Please check your Google Maps API configuration.
          </p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div ref={mapRef} className="w-full h-[500px] md:h-[700px] lg:h-[800px]" />

      {/* Map Legend */}
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
          {[
            { type: 'FQHC', color: '#3B82F6' },
            { type: 'Free Clinic', color: '#10B981' },
            { type: 'Mental Health Center', color: '#8B5CF6' },
            { type: 'Substance Abuse Treatment', color: '#F59E0B' },
            { type: 'Food Bank', color: '#EF4444' },
            { type: 'Housing Support', color: '#6366F1' },
            { type: 'Transportation Provider', color: '#14B8A6' },
            { type: 'ECM Provider', color: '#EC4899' },
          ].map(({ type, color }) => (
            <div key={type} className="flex items-center space-x-2">
              <div
                className="w-3 h-3 rounded-full border-2 border-white"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-gray-600">{type}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Extend window interface for TypeScript
declare global {
  interface Window {
    openProviderDetails: (providerId: string) => void;
  }
}
